/**
 * "Acting as your business Page" — a Facebook-style persona switch on the SAME
 * login (not a separate account). We remember which Pro page you're acting as;
 * the chrome reflects it, messaging sends as the page, and every API request
 * carries it as `X-Pro-Page` (services/api.js) so the server scopes owner
 * reads and writes to that page when the account manages more than one.
 * Cleared on full logout by AuthContext and on account switch by accountRegistry.
 */
const KEY = 'pr.activePage';

/** Same-tab change signal, so the top-bar strip follows a switch made on the dashboard without a reload. */
export const ACTIVE_PAGE_EVENT = 'pr:activepage';

export function getActivePage() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    return raw && raw.slug ? raw : null;
  } catch {
    return null;
  }
}

/** Store a page (any object with id + slug — a /pro/mine row, a ProBusinessDto, or a previous persona) or null to clear. */
export function setActivePage(page) {
  if (page && page.slug) {
    localStorage.setItem(KEY, JSON.stringify({
      id: page.id || null,
      slug: page.slug,
      name: page.name || '',
      avatarUrl: page.avatarUrl || page.logoUrl || null,
      kind: page.kind || null,
      role: page.role || null,
    }));
  } else {
    localStorage.removeItem(KEY);
  }
  try { window.dispatchEvent(new CustomEvent(ACTIVE_PAGE_EVENT, { detail: getActivePage() })); } catch { /* no window (tests without DOM) */ }
}

/** Subscribe to same-tab persona changes. Returns the unsubscribe. */
export function onActivePageChange(fn) {
  const handler = (e) => fn(e?.detail !== undefined ? e.detail : getActivePage());
  window.addEventListener(ACTIVE_PAGE_EVENT, handler);
  return () => window.removeEventListener(ACTIVE_PAGE_EVENT, handler);
}

/** True when `page` is the stored persona — by id when both sides have one, else by slug. */
export function isActivePage(page, active = getActivePage()) {
  if (!page || !active) return false;
  if (active.id && page.id) return active.id === page.id;
  return !!active.slug && active.slug === page.slug;
}
